// "Naprave in seje": where the user is signed in; end one session or all others.
import { useState } from 'react';
import type { SessionInfo } from '../../../../shared/types';
import { listSessions, revokeOtherSessions, revokeSession } from '../../lib/account';
import { accountErrorText } from '../../components/AccountForms';
import { confirmDialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, SessionList, sessionKindLabel, useLoaded } from './common';

export function SessionsSection() {
  const sessions = useLoaded(listSessions, 'Seznama naprav ni bilo mogoče naložiti.');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyAll, setBusyAll] = useState(false);
  const list = sessions.data ?? [];
  const others = list.filter((s) => !s.current).length;

  const revoke = async (s: SessionInfo) => {
    const name = s.deviceName || sessionKindLabel(s);
    const ok = await confirmDialog({
      title: 'Odjavim napravo?',
      message:
        s.kind === 'device'
          ? `Aplikacija Android na napravi »${name}« bo odjavljena. Posnetki, ki še čakajo na prenos, ostanejo na telefonu, dokler se tam znova ne prijavite.`
          : `Prijava »${name}« bo končana. Kdor uporablja to napravo, se bo moral znova prijaviti.`,
      confirmLabel: 'Odjavi',
      danger: true,
    });
    if (!ok) return;
    setBusyId(s.id);
    try {
      await revokeSession(s.id);
      toast('Naprava je odjavljena.', { kind: 'success' });
      sessions.reload();
    } catch (err) {
      toast(accountErrorText(err, 'Naprave ni bilo mogoče odjaviti.'), { kind: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const revokeAll = async () => {
    const ok = await confirmDialog({
      title: 'Odjavim vse druge naprave?',
      message: 'Vse prijave razen te bodo končane – tudi v aplikaciji Android. To naredite, če ste izgubili telefon ali sumite, da kdo pozna vaše geslo (nato spremenite še geslo).',
      confirmLabel: 'Odjavi vse druge',
      danger: true,
    });
    if (!ok) return;
    setBusyAll(true);
    try {
      await revokeOtherSessions();
      toast('Vse druge naprave so odjavljene.', { kind: 'success' });
      sessions.reload();
    } catch (err) {
      toast(accountErrorText(err, 'Naprav ni bilo mogoče odjaviti.'), { kind: 'error' });
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <section className="card" aria-labelledby="sessions-title" id="naprave">
      <div className="card__head">
        <h2 id="sessions-title" className="card__title">
          <Icon name="phone" size={22} /> Naprave in seje
        </h2>
        <button type="button" className="icon-btn" onClick={sessions.reload} aria-label="Osveži seznam naprav" title="Osveži" disabled={sessions.loading}>
          <Icon name={sessions.loading ? 'spinner' : 'refresh'} size={20} />
        </button>
      </div>
      <p className="muted">Naprave in brskalniki, v katerih ste prijavljeni. Če katere ne prepoznate, jo odjavite in spremenite geslo.</p>
      {sessions.error && !sessions.data ? (
        <LoadError message={sessions.error} onRetry={sessions.reload} />
      ) : !sessions.data ? (
        <Loading />
      ) : (
        <>
          <SessionList sessions={list} onRevoke={(s) => void revoke(s)} busyId={busyId} />
          <div className="card__foot">
            <span className="muted small">{others === 0 ? 'Prijavljeni ste samo v tej napravi.' : null}</span>
            <button type="button" className="btn btn--secondary" onClick={() => void revokeAll()} disabled={others === 0 || busyAll}>
              <Icon name={busyAll ? 'spinner' : 'logout'} size={20} /> Odjavi vse druge naprave
            </button>
          </div>
        </>
      )}
    </section>
  );
}
